import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import CollapsibleBoxComponent from '../../../components/CollapsibleBox/CollapsibleBoxComponent';
import TextFieldComponent from '../../../components/TextField/TextFieldComponent';
import DropdownComponent from '../../../components/DropDown/DropDownComponent';
import AddressBarComponent from '../../../components/AddressBar/AddressBarComponent';
import LoadingDots from '../../../components/LoadingDots/LoadingDots';
import personalTemplate from '../../../json/components/subscreens/PersonalDetailsTemplate.json';

const componentMap = {
  'text-field': TextFieldComponent,
  dropdown: DropdownComponent,
  'address-bar': AddressBarComponent,
};

const applyValuesToTemplate = (template, values) => {
  const merged = JSON.parse(JSON.stringify(template));
  Object.entries(values || {}).forEach(([key, value]) => {
    if (merged[key]) {
      merged[key].value = value ?? '';
    }
  });
  return merged;
};

const PersonalDetailsContent = ({
  handleSave,
  resetValues,
  isMobile,
  values = {},
  onFieldChange,
  isSaving = false,
  isResetting = false,
  registerValidation,
}) => {
  const merged = useMemo(
    () => applyValuesToTemplate(personalTemplate, values),
    [values]
  );
  const entries = Object.entries(merged);

  const onChange = (name, value) => {
    onFieldChange?.(name, value);
  };

  return (
    <CollapsibleBoxComponent title="Personal details" isMobile={isMobile}>
      <Box sx={{ p: isMobile ? 1.5 : 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 416px)',
            columnGap: isMobile ? 0 : '32px',
            rowGap: isMobile ? 4 : '24px',
            justifyContent: 'center',
            alignItems: 'start',
          }}
        >
          {entries.map(([name, question]) => {
            const Component = componentMap[question.type];
            if (!Component) return null;

            return (
              <Box key={name} sx={{ width: '100%' }}>
                <Component
                  name={name}
                  question={question}
                  value={values[name]}
                  values={values}
                  onChange={onChange}
                  registerValidation={registerValidation}
                  isMobile={isMobile}
                  layoutVariant="account"
                />
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            color="inherit"
            onClick={resetValues}
            disabled={isResetting}
            sx={{
              backgroundColor: '#E2E5EA',
              px: 4,
              py: 1.5,
              borderRadius: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.8125rem' : '1.25rem',
              color: '#1C2F3A',
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '120px' },
            }}
          >
            {isResetting ? <LoadingDots /> : 'Reset'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            sx={{
              backgroundColor: '#C7CA00',
              px: 4,
              py: 1.5,
              borderRadius: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              fontFamily: 'Inter, sans-serif',
              fontSize: isMobile ? '0.8125rem' : '1.25rem',
              color: '#1C2F3A',
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '205px' },
            }}
          >
            {isSaving ? <LoadingDots /> : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </CollapsibleBoxComponent>
  );
};

export default PersonalDetailsContent;
